import './Items.scss';
import React, {ChangeEvent, useContext} from 'react';
import {useMediaQuery} from 'react-responsive';
import block from 'bem-cn-lite';
import {
    ArticleInfo,
    DeliveryDate,
    DeliveryDays,
    Price,
    ProposalBadges,
    Quantity,
    QuantityInput,
    Rating
} from '@components';
import {Checkbox, Text} from '@gravity-ui/uikit';
import {AuxControls} from './components';
import {formatDate} from '@utils';
import {useBasket} from '@features/basket/hooks/useBasket';
import {BasketItemModel} from '@features/basket/interfaces';
import {AppContext} from 'app/context';
import {getAdjustedPrice} from '@utils/getAdjustedPrice';

const b = block('items');

interface ItemsProps {
    items: BasketItemModel[];
}

export const Items: React.FC<ItemsProps> = ({items}) => {
    const {state} = useContext(AppContext);

    const isSmallScreen = useMediaQuery({query: '(max-width: 1280px)'});

    const {handleItemQuantityChange, handleItemCheckChange} = useBasket();

    // Проверяем, какие события произошли (доступность количества и обнуление)
    const itemsUnavailableOccurred = items.some((item) => item.quantityChangedToZero);
    const itemsCntExceedsQuantityOccurred = items.some((item) => item.quantityDecreased);

    return (
        <div className={b()}>
            {items.map((item) => {
                const [dateStr, timeStr] = formatDate(item.createdAt, [
                    'DD.MM.YY',
                    'HH:mm'
                ]);

                let errorText = '';

                if (itemsUnavailableOccurred && itemsCntExceedsQuantityOccurred) {
                    // Если сработали оба события, для всех товаров отображаем:
                    errorText = `К заказу было ${item.itemsCnt} шт. Осталось ${item.quantity} шт.`;
                } else if (!itemsUnavailableOccurred && itemsCntExceedsQuantityOccurred) {
                    // Если изменилось количество без обнуления
                    if (item.quantityDecreased) {
                        errorText = `К заказу было ${item.itemsCnt} шт. Осталось ${item.quantity} шт.`;
                    }
                }

                const quantityValue =
                    item.quantity > 0 ? Math.min(item.itemsCnt, item.quantity) : 0;

                // Вычисляем старую и новую суммы
                const oldAmount = (item.oldPrice ?? item.price) * quantityValue;
                const newAmount = item.oldPrice ? item.price * quantityValue : undefined;

                // Определяем цвет для отображения новой цены
                let priceColor: 'warning' | 'positive' | undefined;
                if (item.oldPrice) {
                    if (item.price > item.oldPrice) {
                        priceColor = 'warning'; // Цена увеличилась
                    } else if (item.price < item.oldPrice) {
                        priceColor = 'positive'; // Цена уменьшилась
                    }
                }

                const adjustedValue = getAdjustedPrice(
                    item.oldPrice ?? item.price,
                    state.priceLevelSettings,
                    'basket'
                );

                const adjustedNewValue = item.oldPrice
                    ? getAdjustedPrice(item.price, state.priceLevelSettings, 'basket')
                    : undefined;

                const adjustedOldAmount = getAdjustedPrice(
                    oldAmount,
                    state.priceLevelSettings,
                    'basket'
                );
                const adjustedNewAmount = getAdjustedPrice(
                    newAmount ?? 0,
                    state.priceLevelSettings,
                    'basket'
                );

                return (
                    <div key={item.id} className={b('row')}>
                        <div className={b('item', b('is-selected'))}>
                            <Checkbox
                                size={'l'}
                                checked={item.checked}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    handleItemCheckChange(item.id, event.target.checked)
                                }
                            ></Checkbox>
                        </div>

                        {!isSmallScreen && (
                            <div className={b('item', b('date'))}>
                                <Text variant={'body-2'}>{dateStr}</Text>
                                <Text variant={'body-2'} className={b('date', 'time')}>
                                    {timeStr}
                                </Text>
                            </div>
                        )}

                        <ArticleInfo
                            className={b('item', b('article'))}
                            header={{
                                article: item.article,
                                articleName: item.articleName,
                                brandName: item.brandName
                            }}
                        />

                        <ProposalBadges
                            className={b('item', b('icons'))}
                            isOfficialDealer={item.isOfficialDealer}
                            isReturnPossible={item.isReturnPossible}
                        />

                        <Rating value={item.rating} priceList={item.proposal?.priceList} className={b('item', b('rating'))} />

                        {!isSmallScreen && (
                            <Quantity className={b('item', b('quantity'))} value={item.quantity} />
                        )}

                        <DeliveryDays
                            value={item.deliveryDays}
                            className={b('item', b('delivery-days'))}
                        />

                        <DeliveryDate
                            value={item.deliveryDate}
                            className={b('item', b('delivery-date'))}
                        />

                        <Price
                            value={adjustedValue}
                            newValue={adjustedNewValue}
                            className={b('item', b('price'))}
                            color={priceColor}
                        />

                        <Price
                            value={adjustedOldAmount}
                            newValue={adjustedNewAmount}
                            className={b('item', b('amount'))}
                            color={priceColor}
                        />

                        <QuantityInput
                            value={quantityValue}
                            multiplicity={item.multiplicity || 1}
                            maxQuantity={item.quantity}
                            onChange={(newQuantity) =>
                                handleItemQuantityChange(item.id, newQuantity)
                            }
                            className={b('item', b('quantity-input'))}
                            errorText={errorText}
                        />

                        <AuxControls item={item} />
                    </div>
                );
            })}
        </div>
    );
};
